import React, { useEffect, useRef } from 'react';
import apiClient from '../api/client';
import { usePageCacheState } from '../contexts/PageCacheContext';
import {
  FileText,
} from 'lucide-react';

import { Template, TemplateCategory, FillTemplateKey } from '../components/templates/types';
import TemplateStats from '../components/templates/TemplateStats';
import TemplateFilter from '../components/templates/TemplateFilter';
import TemplateCard from '../components/templates/TemplateCard';
import UploadModal from '../components/templates/UploadModal';
import FillModal from '../components/templates/FillModal';
import AddCategoryModal from '../components/templates/AddCategoryModal';
import DeleteConfirmModal from '../components/templates/DeleteConfirmModal';
import SubscriptionGuard from '../components/common/SubscriptionGuard';

const TemplateSystemContent: React.FC = () => {
  // Manual refresh auth function to avoid duplicate profile requests
  const refreshAuth = React.useCallback(async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return false;

      const response = await apiClient.get('/user/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.success;
    } catch (error) {
      localStorage.removeItem('authToken');
      return false;
    }
  }, []);
  const [templates, setTemplates] = usePageCacheState<Template[]>(
    'templatesystem.templates',
    []
  );
  const [categories, setCategories] = usePageCacheState<TemplateCategory[]>(
    'templatesystem.categories',
    []
  );
  const [selectedCategory, setSelectedCategory] = usePageCacheState<string>(
    'templatesystem.selectedCategory',
    'all'
  );

  const [selectedTemplate, setSelectedTemplate] =
    usePageCacheState<Template | null>('templatesystem.selectedTemplate', null);
  const [showUploadModal, setShowUploadModal] = usePageCacheState<boolean>(
    'templatesystem.showUploadModal',
    false
  );
  const [showFillModal, setShowFillModal] = usePageCacheState<boolean>(
    'templatesystem.showFillModal',
    false
  );
  const [showAddCategoryModal, setShowAddCategoryModal] =
    usePageCacheState<boolean>('templatesystem.showAddCategoryModal', false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] =
    usePageCacheState<boolean>('templatesystem.showDeleteConfirmModal', false);
  const [categoryToDelete, setCategoryToDelete] = usePageCacheState<{
    id: string;
    name: string;
  } | null>('templatesystem.categoryToDelete', null);
  const [searchTerm, setSearchTerm] = usePageCacheState<string>(
    'templatesystem.searchTerm',
    ''
  );
  const [isDragging, setIsDragging] = usePageCacheState<boolean>(
    'templatesystem.isDragging',
    false
  );
  const [loading, setLoading] = usePageCacheState<boolean>(
    'templatesystem.loading',
    true
  );
  const [uploading, setUploading] = usePageCacheState<boolean>(
    'templatesystem.uploading',
    false
  );
  const [error, setError] = usePageCacheState<string | null>(
    'templatesystem.error',
    null
  );
  const [uploadForm, setUploadForm] = usePageCacheState(
    'templatesystem.uploadForm',
    {
      name: '',
      description: '',
      category_id: '',
      file: null as File | null,
    }
  );

  const [newCategoryForm, setNewCategoryForm] = usePageCacheState(
    'templatesystem.newCategoryForm',
    {
      name: '',
      description: '',
      color: '#3B82F6',
    }
  );

  const [templateKeys, setTemplateKeys] = usePageCacheState<FillTemplateKey[]>(
    'templatesystem.templateKeys',
    []
  );
  const [generatedDocument, setGeneratedDocument] = usePageCacheState<
    string | null
  >('templatesystem.generatedDocument', null);
  const [generatedDocumentData, setGeneratedDocumentData] = usePageCacheState<
    any
  >('templatesystem.generatedDocumentData', null);
  const initialFetchRef = useRef(false);

  const loadCategories = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await apiClient.get('/template-categories', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      // Kategori yüklenirken hata oluştu
    }
  }, [setCategories]);


  const createNewCategory = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      if (!newCategoryForm.name.trim()) {
        alert('Kategori adı gereklidir');
        return;
      }

      const response = await apiClient.post('/template-categories', {
        name: newCategoryForm.name.trim(),
        description: newCategoryForm.description.trim() || null,
        color: newCategoryForm.color
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Add new category to list
        const newCategory = {
          id: response.data.data.id,
          user_id: '',
          name: response.data.data.name,
          description: response.data.data.description,
          color: response.data.data.color,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setCategories([newCategory, ...categories]);
        // Auto-select the new category
        setUploadForm(prev => ({ ...prev, category_id: newCategory.id }));
        // Close modal and reset form
        setShowAddCategoryModal(false);
        setNewCategoryForm({ name: '', description: '', color: '#3B82F6' });
        alert(`${newCategory.name} kategorisi oluşturuldu ve seçildi!`);
      }
    } catch (error: any) {
      if (error.response?.data?.error?.message?.includes('duplicate key')) {
        // Try to load categories again and find the existing one
        await loadCategories();
        // Use setTimeout to ensure categories state is updated
        setTimeout(async () => {
          // Refetch categories to get the latest state
          const response = await apiClient.get('/template-categories', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (response.data.success) {
            const updatedCategories = response.data.data;
            const existingCategory = updatedCategories.find((cat: any) => cat.name === newCategoryForm.name.trim());
            if (existingCategory) {
              setUploadForm(prev => ({ ...prev, category_id: existingCategory.id }));
              alert(`${existingCategory.name} kategorisi zaten mevcut ve seçildi!`);
            }
          }
        }, 100);
      } else {
        alert('Kategori oluşturulurken hata oluştu: ' + (error.response?.data?.error?.message || error.message));
      }
    }
  };

  const showDeleteConfirmation = (categoryId: string, categoryName: string) => {
    setCategoryToDelete({ id: categoryId, name: categoryName });
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await apiClient.delete(`/template-categories/${categoryToDelete.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        // Remove category from list
        setCategories(categories.filter(cat => cat.id !== categoryToDelete.id));
        // If the deleted category was selected in upload form, clear it
        if (uploadForm.category_id === categoryToDelete.id) {
          setUploadForm(prev => ({ ...prev, category_id: '' }));
        }
        alert(`${categoryToDelete.name} kategorisi silindi!`);
      }
    } catch (error: any) {
      if (error.response?.data?.error?.code === 'CATEGORY_HAS_TEMPLATES') {
        alert(error.response.data.error.message);
      } else if (error.response?.data?.error?.code === 'CATEGORY_NOT_FOUND') {
        alert('Kategori bulunamadı veya zaten silinmiş');
        // Remove from local state anyway
        setCategories(categories.filter(cat => cat.id !== categoryToDelete.id));
      } else {
        alert('Kategori silinirken hata oluştu: ' + (error.response?.data?.error?.message || error.message));
      }
    } finally {
      setShowDeleteConfirmModal(false);
      setCategoryToDelete(null);
    }
  };

  const loadTemplates = React.useCallback(async (retryCount = 0, force = false) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Giriş yapmanız gerekiyor');
        return;
      }

      if (force || templates.length === 0) {
        setLoading(true);
      }

      const response = await apiClient.get('/templates', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 10000
      });

      if (response.data.success) {
        setTemplates(response.data.data || []);
        setError(null);
      }
    } catch (error: any) {
      if (error.response?.status === 401 && retryCount === 0) {
        const authRefreshed = await refreshAuth();
        if (authRefreshed) {
          setTimeout(() => loadTemplates(1, force), 1000);
          return;
        }
      }

      setError('Template\'ler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [refreshAuth, setError, setTemplates, setLoading, templates.length]);

  // Load templates on component mount
  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;
    loadTemplates();
    loadCategories();
  }, [loadTemplates, loadCategories]);

  const getCategoryCount = (categoryId: string) => {
    if (categoryId === 'all') return templates.length;
    if (categoryId === 'uncategorized') return templates.filter(t => !t.category_id).length;
    return templates.filter(t => t.category_id === categoryId).length;
  };

  const openFillModal = (template: Template) => {
    setSelectedTemplate(template);

    // Generate template keys with default values
    const keys: FillTemplateKey[] = (template.Keys || []).map(key => ({
      key_name: key.key_name,
      value: key.default_value || '',
      data_type: key.data_type,
      is_required: key.is_required
    }));

    setTemplateKeys(keys);
    setGeneratedDocument(null);
    setShowFillModal(true);
  };


  const handleKeyValueChange = (index: number, value: string) => {
    const updatedKeys = [...templateKeys];
    updatedKeys[index].value = value;
    setTemplateKeys(updatedKeys);

    // Real-time preview update - trigger a re-render
    // The preview will automatically update because it depends on templateKeys state
  };

  const generateDocument = async (): Promise<void> => {
    if (!selectedTemplate) return;

    // Validate required fields
    const filledKeys = templateKeys.filter(key => key.value.trim() !== '');
    if (filledKeys.length === 0) {
      alert('Lütfen en az bir alanı doldurun.');
      return;
    }

    setGeneratedDocument('processing');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Giriş yapmanız gerekiyor');
        setGeneratedDocument(null);
        return;
      }

      const keyValues: { [key: string]: string } = {};
      templateKeys.forEach(key => {
        if (key.value) {
          keyValues[key.key_name] = key.value;
        }
      });

      const response = await apiClient.post(
        `/templates/${selectedTemplate.id}/generate`,
        {
          key_values: keyValues
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const generatedDoc = response.data.data;
        setGeneratedDocument(generatedDoc.filename);
        setGeneratedDocumentData(generatedDoc);
      } else {
        throw new Error('Document generation failed');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        await refreshAuth();
        return generateDocument();
      }
      alert('Döküman oluşturulamadı!');
      setGeneratedDocument(null);
    }
  };

  // Download original template
  const downloadTemplate = async (template: Template): Promise<void> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Giriş yapmanız gerekiyor');
        return;
      }

      const response = await apiClient.get(`/templates/${template.id}/download`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', template.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      if (error.response?.status === 401) {
        await refreshAuth();
        return downloadTemplate(template);
      }
      alert('Template indirilemedi!');
    }
  };

  const downloadDocument = async (): Promise<void> => {
    if (!generatedDocumentData) {
      alert('İndirilecek döküman bulunamadı!');
      return;
    }

    try {
      // Download generated document using file_path
      const response = await apiClient.get(generatedDocumentData.download_url, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', generatedDocumentData.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Close modal after download
      setTimeout(() => {
        setShowFillModal(false);
      }, 1000);
    } catch (error: any) {
      if (error.response?.status === 401) {
        await refreshAuth();
        return downloadDocument();
      }
      alert('Döküman indirilemedi!');
    }
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.name.endsWith('.docx')) {
      alert('Sadece .docx dosyaları yüklenebilir.');
      return;
    }

    if (file.size > 20 * 1024 * 1024) { // 20MB
      alert('Dosya boyutu 20MB\'dan büyük olamaz.');
      return;
    }

    // Set file in form and suggest name
    const suggestedName = file.name.replace('.docx', '');
    setUploadForm({
      name: uploadForm.name || suggestedName,
      description: uploadForm.description,
      category_id: uploadForm.category_id,
      file: file
    });

    // If no name is set, we'll show the form
    if (!uploadForm.name) {
      return;
    }

    // Proceed with upload
    await performUpload(file, uploadForm.name, uploadForm.description);
  };

  const performUpload = async (file: File, name: string, description: string) => {
    setUploading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Giriş yapmanız gerekiyor');
        return;
      }

      const formData = new FormData();
      formData.append('template', file);
      formData.append('name', name);
      formData.append('description', description || 'Yeni yüklenen şablon');
      if (uploadForm.category_id) {
        formData.append('category_id', uploadForm.category_id);
      }

      const response = await apiClient.post('/templates', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30000 // 30 seconds for file upload
      });

      if (response.data.success) {
        // Add new template to list
        setTemplates([response.data.data, ...templates]);
        setShowUploadModal(false);
        setUploadForm({ name: '', description: '', category_id: '', file: null });
        alert('Template başarıyla yüklendi!');
      }
    } catch (error: any) {
      let errorMessage = 'Template yüklenirken hata oluştu';
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }

      // Show user-friendly error message
      if (error.response?.data?.error?.code === 'DUPLICATE_TEMPLATE') {
        alert('⚠️ ' + errorMessage + '\n\nLütfen farklı bir isim kullanın veya mevcut template\'i güncelleyin.');
      } else {
        alert('❌ ' + errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const deleteTemplate = async (id: string) => {
    if (!window.confirm('Bu şablonu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Giriş yapmanız gerekiyor');
        return;
      }

      const response = await apiClient.delete(`/templates/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setTemplates(templates.filter(t => t.id !== id));
        alert('Template başarıyla silindi!');
      }
    } catch (error: any) {
      alert('Template silinirken hata oluştu: ' + (error.response?.data?.error?.message || error.message));
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.description && template.description.toLowerCase().includes(searchTerm.toLowerCase()));

    let matchesCategory = true;
    if (selectedCategory === 'all') {
      matchesCategory = true;
    } else if (selectedCategory === 'uncategorized') {
      matchesCategory = !template.category_id;
    } else {
      matchesCategory = template.category_id === selectedCategory;
    }

    return matchesSearch && matchesCategory && template.is_active;
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Hiç kullanılmadı';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded-lg w-32 animate-pulse"></div>
        </div>

        {/* Search and Filter Skeleton */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded-lg w-32 animate-pulse"></div>
        </div>

        {/* Template Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-xl border animate-pulse">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                  <div className="space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-32"></div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
                <div className="w-6 h-6 bg-gray-200 rounded"></div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="h-3 bg-gray-200 rounded w-full"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
              </div>

              <div className="flex justify-between items-center mb-4">
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>

              <div className="flex space-x-2">
                <div className="flex-1 h-9 bg-gray-200 rounded-lg"></div>
                <div className="h-9 w-9 bg-gray-200 rounded-lg"></div>
                <div className="h-9 w-9 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <p className="text-lg font-medium">Hata</p>
          <p className="text-sm">{error}</p>
        </div>
        <button
          onClick={() => loadTemplates(0, true)}
          className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <FileText className="h-8 w-8 mr-3 text-blue-600" />
            Şablon Yönetimi
          </h1>
          <p className="mt-2 text-gray-600">
            Word şablonlarınızı yükleyin, alanları doldurun ve otomatik dökümanlar oluşturun.
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors flex items-center font-medium"
        >
          <FileText className="h-5 w-5 mr-2" />
          Şablon Yükle
        </button>
      </div>

      <TemplateStats templates={templates} />

      <TemplateFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        getCategoryCount={getCategoryCount}
        showDeleteConfirmation={showDeleteConfirmation}
      />

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            onFill={openFillModal}
            onDownload={downloadTemplate}
            onDelete={deleteTemplate}
            formatDate={formatDate}
          />
        ))}
      </div>

      {filteredTemplates.length === 0 && !loading && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Şablon bulunamadı</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm ? 'Arama kriterlerinize uygun şablon bulunamadı.' : 'Henüz şablon yüklenmemiş.'}
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors"
          >
            İlk Şablonu Yükle
          </button>
        </div>
      )}

      {showUploadModal && (
        <UploadModal
          onClose={() => {
            setShowUploadModal(false);
            setUploadForm({ name: '', description: '', category_id: '', file: null });
          }}
          uploadForm={uploadForm}
          setUploadForm={setUploadForm}
          categories={categories}
          isDragging={isDragging}
          uploading={uploading}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onFileUpload={handleFileUpload}
          onPerformUpload={performUpload}
          onShowAddCategoryModal={() => setShowAddCategoryModal(true)}
        />
      )}

      {showAddCategoryModal && (
        <AddCategoryModal
          onClose={() => {
            setShowAddCategoryModal(false);
            setNewCategoryForm({ name: '', description: '', color: '#3B82F6' });
          }}
          newCategoryForm={newCategoryForm}
          setNewCategoryForm={setNewCategoryForm}
          onCreateCategory={createNewCategory}
        />
      )}

      {showDeleteConfirmModal && categoryToDelete && (
        <DeleteConfirmModal
          onClose={() => {
            setShowDeleteConfirmModal(false);
            setCategoryToDelete(null);
          }}
          onConfirm={confirmDeleteCategory}
          title="Kategori Sil"
          message="Bu işlem geri alınamaz"
          itemName={categoryToDelete.name}
        />
      )}

      {showFillModal && selectedTemplate && (
        <FillModal
          selectedTemplate={selectedTemplate}
          onClose={() => setShowFillModal(false)}
          templateKeys={templateKeys}
          handleKeyValueChange={handleKeyValueChange}
          generateDocument={generateDocument}
          generatedDocument={generatedDocument}
          downloadDocument={downloadDocument}
        />
      )}
    </div>
  );
};

const TemplateSystem: React.FC = () => (
  <SubscriptionGuard requiredPlan="template">
    <TemplateSystemContent />
  </SubscriptionGuard>
);

export default TemplateSystem;
